import type { IconName } from '@/components/common/Icon';
import { MODULE_KEY } from '@/constants/modules';

export type WorkspaceSettingsTabId =
  | 'patientOptions'
  | 'treatmentLibrary'
  | 'services'
  | 'consultations'
  | 'planTemplates'
  | 'consentDocuments'
  | 'branding'
  | 'integrations'
  | 'notificationPreferences'
  | 'assistant'
  | 'myAccount';

export interface WorkspaceSettingsTabVisibilityContext {
  enabledModules: string[];
  isOwner: boolean;
  canCustomizeAssistant: boolean;
}

export interface WorkspaceSettingsTabDefinition {
  id: WorkspaceSettingsTabId;
  iconName: IconName;
  labelKey: string;
  requiredModule?: string;
  ownerOnly?: boolean;
  visibleWhen?: (context: WorkspaceSettingsTabVisibilityContext) => boolean;
}

export const WORKSPACE_SETTINGS_TABS: WorkspaceSettingsTabDefinition[] = [
  {
    id: 'patientOptions',
    iconName: 'PawPrint',
    labelKey: 'settings:practitioner.tabs.patientOptions',
  },
  {
    id: 'treatmentLibrary',
    iconName: 'BookOpen',
    labelKey: 'settings:practitioner.tabs.treatmentLibrary',
    requiredModule: 'treatment_library',
  },
  {
    id: 'services',
    iconName: 'Tag',
    labelKey: 'settings:practitioner.tabs.services',
    requiredModule: 'services',
    ownerOnly: true,
  },
  {
    id: 'consultations',
    iconName: 'ClipboardCheck',
    labelKey: 'settings:practitioner.tabs.consultations',
    requiredModule: 'first_consultation',
  },
  {
    id: 'planTemplates',
    iconName: 'FileText',
    labelKey: 'settings:practitioner.tabs.planTemplates',
    requiredModule: 'treatment_plans',
  },
  {
    id: 'consentDocuments',
    iconName: 'ShieldCheck',
    labelKey: 'settings:practitioner.tabs.consentDocuments',
    requiredModule: 'consents',
    ownerOnly: true,
  },
  {
    id: 'branding',
    iconName: 'Palette',
    labelKey: 'settings:practitioner.tabs.branding',
    requiredModule: MODULE_KEY.branding,
    ownerOnly: true,
  },
  {
    id: 'integrations',
    iconName: 'Link',
    labelKey: 'settings:practitioner.tabs.integrations',
    visibleWhen: function isIntegrationsVisible(context) {
      const hasCalendarSync = context.enabledModules.includes(MODULE_KEY.calendarSync);
      const hasNotificationProviders =
        context.isOwner === true && context.enabledModules.includes(MODULE_KEY.communications);
      return hasCalendarSync === true || hasNotificationProviders === true;
    },
  },
  {
    id: 'notificationPreferences',
    iconName: 'Bell',
    labelKey: 'settings:practitioner.tabs.notificationPreferences',
  },
  {
    id: 'assistant',
    iconName: 'Sparkles',
    labelKey: 'settings:practitioner.tabs.assistant',
    visibleWhen: function isAssistantVisible(context) {
      return context.canCustomizeAssistant;
    },
  },
  {
    id: 'myAccount',
    iconName: 'User',
    labelKey: 'settings:practitioner.tabs.myAccount',
  },
];
